/* ==========================================================================
   app.js — orchestration / event wiring
   Boots the market, wires the switcher, runs the finder + map, reveals.
   ========================================================================== */

(function () {
  const { market, maps } = window.NATY;

  /* ---------- 1. Boot the market ---------- */
  market.set(market.resolve(), false);

  /* ---------- 2. Market switcher ---------- */
  const sw = document.querySelector('[data-market-switch]');
  if (sw) {
    const swBtn = sw.querySelector('[data-switch-btn]');
    const swMenu = sw.querySelector('[data-switch-menu]');

    const closeMenu = () => {
      swMenu.hidden = true;
      swBtn.setAttribute('aria-expanded', 'false');
    };
    swBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = swMenu.hidden;
      swMenu.hidden = !open;
      swBtn.setAttribute('aria-expanded', String(open));
    });
    swMenu.querySelectorAll('[data-market-option]').forEach((li) => {
      li.addEventListener('click', () => {
        const code = li.getAttribute('data-market-option');
        market.set(code);
        window.NATY.track.switchMarket(code);
        resetFinder();
        closeMenu();
      });
    });
    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
  }

  /* ---------- 3. Finder + map ---------- */
  const form = document.querySelector('[data-finder-form]');
  const input = document.querySelector('[data-location-input]');
  const geoBtn = document.querySelector('[data-use-location]');
  const resultsEl = document.querySelector('[data-results]');
  const placeholder = document.querySelector('[data-map-placeholder]');

  // Warm the lazy MapLibre download on first finder intent (focus/hover) so the
  // ≈800 KB is already in flight by the time the visitor actually searches.
  // Not `once`: after a market switch the next intent warms that market's stores.
  const warmMap = () => maps.preload(market.current.code).catch(() => {});
  if (input) input.addEventListener('focus', warmMap);
  if (geoBtn) geoBtn.addEventListener('pointerenter', warmMap);

  function resetFinder() {
    resultsEl.innerHTML = '';
    if (input) input.value = '';
    maps.reset(market.current);
    placeholder.classList.remove('is-hidden');
  }

  function setState(html) {
    resultsEl.innerHTML = `<div class="results__state">${html}</div>`;
  }

  function loading() {
    setState(`<div class="spinner"></div><p>${market.current.strings.searching}</p>`);
  }

  function showError(msg) {
    setState(`<p>${msg}</p>`);
  }

  function showEmpty() {
    const m = market.current;
    const s = m.strings;
    setState(
      `<h4>${s.emptyTitle}</h4><p>${s.emptyLead}</p>` +
      `<a class="btn btn--primary" href="${m.amazonUrl}" target="_blank" rel="noopener" ` +
      `onclick="window.NATY.track.amazonClick('${m.code}')">${s.amazonCta}</a>`
    );
  }

  let allRows = [];
  let shown = 0;
  let listEl = null;
  let moreBtn = null;

  function rowEl(s, i) {
    const m = market.current;
    // Hands off to the visitor's default maps app, using their location as origin.
    const dir = `https://www.google.com/maps/dir/?api=1&destination=${s.pos.lat},${s.pos.lng}`;
    const el = document.createElement('div');
    el.className = 'result';
    el.innerHTML =
      `<span class="result__pin">${i + 1}</span>` +
      `<div class="result__body">` +
      `<div class="result__chain">${s.chain}</div>` +
      `<div class="result__name">${s.name}</div>` +
      (s.address ? `<div class="result__addr">${s.address}</div>` : '') +
      `</div>` +
      `<div class="result__meta">` +
      `<span class="result__dist">${s.distanceLabel}</span>` +
      (s.etaLabel ? `<span class="result__eta">${s.etaLabel}</span>` : '') +
      `<a class="result__dir" href="${dir}" target="_blank" rel="noopener">${m.strings.directions}</a>` +
      `</div>`;
    return el;
  }

  function showMore() {
    const m = market.current;
    allRows.slice(shown, shown + 5).forEach((s, k) => listEl.appendChild(rowEl(s, shown + k)));
    shown = Math.min(shown + 5, allRows.length);
    moreBtn.style.display = shown >= allRows.length ? 'none' : '';
  }

  function renderResults(stores) {
    if (!stores.length) return showEmpty();
    const m = market.current;
    allRows = stores;
    shown = 0;
    resultsEl.innerHTML = `<div class="results__title">${m.strings.closestTitle}</div>`;
    listEl = document.createElement('div');
    listEl.className = 'results__list';
    resultsEl.appendChild(listEl);

    moreBtn = document.createElement('button');
    moreBtn.type = 'button';
    moreBtn.className = 'btn btn--ghost results__more';
    moreBtn.textContent = m.strings.loadMore;
    moreBtn.addEventListener('click', showMore);
    resultsEl.appendChild(moreBtn);

    showMore(); // first batch (5)
  }

  async function search(origin, method) {
    const m = market.current;
    loading();
    placeholder.classList.add('is-hidden');
    window.NATY.track.mapSearch(m.code, method);
    try {
      const stores = await maps.runSearch(origin, m, m.distanceUnit);
      // Ignore a result that lands after the visitor switched market mid-search.
      if (market.current === m) renderResults(stores);
    } catch (err) {
      if (market.current === m) showError(m.strings.errorSearch);
      placeholder.classList.remove('is-hidden');
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) return;
    const m = market.current;
    loading();
    try {
      const origin = await maps.geocode(q, m.country);
      if (market.current === m) search(origin, 'postcode');
    } catch (err) {
      if (market.current === m) showError(m.strings.errorLocation);
    }
  });

  geoBtn.addEventListener('click', async () => {
    loading();
    try {
      const origin = await maps.browserLocate();
      search(origin, 'geolocation');
    } catch (err) {
      showError(market.current.strings.errorGeo);
    }
  });

  /* ---------- 4. Reveal on load + scroll ---------- */
  const io = new IntersectionObserver(
    (entries) => entries.forEach((en) => { if (en.isIntersecting) en.target.classList.add('is-in'); }),
    { threshold: 0.12 }
  );
  document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
})();
